/**
 * Mirrors wpcursor-admin-api's module catalogue. The server is always the real
 * boundary — this exists so the UI can hide what a user cannot use, which is a
 * courtesy, not a security control.
 */

export type AccessLevel = 'none' | 'read' | 'write';

export const ACCESS_RANK: Record<AccessLevel, number> = { none: 0, read: 1, write: 2 };

export type PermissionGrid = Record<string, AccessLevel>;

export interface ModuleDef {
  key: string;
  name: string;
  description: string;
  readOnly?: boolean;
  tier: 'standard' | 'sensitive' | 'critical';
}

/** Absent module = denied. Superadmin short-circuits before this is called. */
export function satisfies(
  grid: PermissionGrid,
  module: string,
  required: AccessLevel = 'read',
): boolean {
  return ACCESS_RANK[grid[module] ?? 'none'] >= ACCESS_RANK[required];
}

/** The Sites & Sandboxes module (list/details = read, destructive actions = write). */
export const SITES_MODULE = 'sites';

/**
 * May this user destroy a sandbox from the Sites page?
 *
 * Needs BOTH grants: `sites:write` (the destructive-action permission of this
 * section) and `trials:write`, because the only existing safe route that tears
 * a sandbox down, DELETE /api/trial/:id, is gated on `trials:write` server-side.
 * Requiring both here means the button never appears for a click the server
 * would refuse.
 */
export function canDestroySandbox(can: (module: string, level?: AccessLevel) => boolean): boolean {
  return can(SITES_MODULE, 'write') && can('trials', 'write');
}

/**
 * Extend (+2h) and Keep forever: the backend gates both on trials:write
 * (POST /api/trial/:id/extend|persist), so the buttons follow the same rule.
 */
export function canManageSandbox(can: (module: string, level?: AccessLevel) => boolean): boolean {
  return can('trials', 'write');
}

/**
 * A site's plugin release channel (test / beta / public). The backend gates
 * PUT /api/license/admin/sites/:id/channel on releases:write, so the control
 * follows the same rule wherever it is shown.
 */
export function canChangeSiteChannel(can: (module: string, level?: AccessLevel) => boolean): boolean {
  return can('releases', 'write');
}
